/**
 * recovery.js
 */

import React from 'react';
import axios from 'axios';
import {
  useMediaQuery,
  styled,
  Container,
  Box,
  Typography,
  TableContainer as MuiTableContainer,
  Table,
  TableHead as MuiTableHead,
  TableBody,
  TableRow as MuiTableRow,
  TableCell as MuiTableCell,
  Paper,
  Tooltip,
  Card,
  CardContent,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Link as MuiLink,
} from '@mui/material';
import { format } from 'date-fns';
import { useSnackbar } from 'notistack';
import { Link } from 'react-router-dom';
import { useDropzone } from 'react-dropzone';
import { objectToFormData } from "utils/form-data";
import { Types } from 'utils/constants';
import { isUsingTouchInput } from 'utils/app-context';
import { makeLocation } from 'utils/routing';
import { AppBarContext } from 'contexts/app-bar/app-bar-context';
import PageTitle from 'components/page-title';
import FeatureBox from 'components/feature-box';
import Icon from 'components/icon';
import Button from 'components/button';
import BarButton from 'components/layout/app-bar/bar-button';
import ConfirmDialog from 'components/confirm-dialog';


const RECOVERY_TYPES = {
  ACCOUNT: 1,
  DEPARTMENT_LOGO: 2,
  DOG: 3,
  DOG_PHOTO: 4,
  TRAINING: 5,
  DEPLOYMENT: 6,
  INDICATION: 7,
  CLASS: 8,
};

const RECOVERY_TYPE_FILE_FIELD_NAMES = {
  [RECOVERY_TYPES.ACCOUNT]: 'userFile',
  [RECOVERY_TYPES.DOG]: 'dogFile',
  [RECOVERY_TYPES.TRAINING]: 'completionFile',
  [RECOVERY_TYPES.DEPLOYMENT]: 'deploymentFile',
  [RECOVERY_TYPES.CLASS]: 'classFile',
};



function truncate(str, n = 28) {
  return (str.length > n) ? str.slice(0, n - 1) + '...' : str;
};


const TableContainer = styled(MuiTableContainer)(({ theme }) => ({
  [theme.breakpoints.down('md')]: {
    overflowX: 'hidden',
    '& table, & tbody, & tr': {
      display: 'block',
    },
    '& td': {
      overflowX: 'auto',
    },
  }
}));


const TableHead = styled(MuiTableHead)(({ theme }) => ({
  [theme.breakpoints.down('md')]: {
    display: 'none',
  }
}));


const TableRow = styled(MuiTableRow)(({ theme }) => ({
  '& ~ &': {
    [theme.breakpoints.down('md')]: {
      borderTop: '1px solid',
    },
  }
}));


const TableCell = styled(MuiTableCell)(({ theme }) => ({
  whiteSpace: 'nowrap',
  [theme.breakpoints.down('md')]: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  }
}));


const FileTableCell = styled(TableCell)(({ theme }) => ({
  [theme.breakpoints.up('md')]: {
    width: '50%',
  }
}));


const ActionTableCell = styled(TableCell)(({ theme }) => ({
  [theme.breakpoints.up('md')]: {
    width: 48,
    minWidth: 48,
    height: 36,
    boxSizing: 'content-box',
    padding: 0,
  }
}));


const MobileLabel = styled('div')(({ theme }) => ({
  fontWeight: 500,
  marginRight: theme.spacing(2),
  [theme.breakpoints.up('md')]: {
    display: 'none',
  }
}));


const ActionButton = styled(Button)(({ theme }) => ({
  width: '100%',
  minWidth: '100%',
  [theme.breakpoints.down('md')]: {
    paddingLeft: 0,
    paddingRight: 0,
  },
}));


const SectionTitle = (props) => {
  return <Typography variant='h6' sx={{ marginTop: 5 }}>{props.children}</Typography>
};


const LinkWidget = (props) => {
  const { to } = props;

  const label = 'View file location';

  return <>
    <MobileLabel>{label}</MobileLabel>
    <Tooltip
      arrow
      title={label}
    >
      <Box>
        <ActionButton
          component={Link}
          to={to}
        >
          <Icon type='open-link' />
        </ActionButton>
      </Box>
    </Tooltip>
  </>;
}


const uploadFile = async ({ recoveryId, recoveryType, file, onSuccess, onEnd }) => {
  const formData = objectToFormData({ file, type: recoveryType });

  try {
    const response = await axios({
      method: 'put',
      url: `/recovery/${recoveryId}/replace`,
      data: formData
    });

    if (response.isSuccess) {
      onSuccess && onSuccess();
    }
  } finally {
    onEnd && onEnd();
  }
};


const ReplaceWidget = (props) => {
  const { recoveryId, imageOnly, recoveryType, onReplacingStart, onReplacingEnd, onReplaced } = props;
  const [isLoading, setIsLoading] = React.useState(false);
  const [isReplaced, setIsReplaced] = React.useState(false);
  const { enqueueSnackbar } = useSnackbar();


  const { getRootProps, getInputProps, open: openDropzone } = useDropzone({
    multiple: false,
    noClick: true,
    noKeyboard: true,
    accept: imageOnly ? 'image/*' : undefined,
    onDropAccepted: (files) => {
      setIsLoading(true);
      onReplacingStart();
      const file = files[0];

      uploadFile({
        recoveryId,
        recoveryType,
        file,
        onSuccess: () => {
          setIsReplaced(true);
          onReplaced();
          enqueueSnackbar('File replaced', {
            variant: 'success'
          });
        },
        onEnd: () => {
          setIsLoading(false);
          onReplacingEnd();
        },
      });
    },
  });


  const label = isLoading
    ? 'Uploading...'
    : 'Upload replacement';


  return <>
    <MobileLabel>{label}</MobileLabel>
    <Tooltip
      arrow
      title={label}
    >
      <FeatureBox loading={isLoading || isReplaced} contentLoadingOpacity={0}>
        <input {...getInputProps()} />
        <Box
          {...getRootProps()}
          onClick={openDropzone}
        >
          <ActionButton
            color='primary'
          >
            <Icon type='upload' />
          </ActionButton>
        </Box>
      </FeatureBox>
    </Tooltip>
  </>;
}


const DeleteWidget = (props) => {
  const { recoveryId, recoveryType, onDeletingStart, onDeletingEnd, onDeleted } = props;
  const [isLoading, setIsLoading] = React.useState(false);
  const [isDeleted, setIsDeleted] = React.useState(false);
  const [confirmDeleteFile, setConfirmDeleteFile] = React.useState();
  const { enqueueSnackbar } = useSnackbar();


  const handleClick = async () => {
    if (await confirmDeleteFile()) {
      setIsLoading(true);
      onDeletingStart();

      try {
        const response = await axios({
          method: 'delete',
          url: `/recovery/${recoveryId}/delete-file`,
          data: { type: recoveryType }
        });

        if (response.isSuccess) {
          setIsDeleted(true);
          onDeleted();
          enqueueSnackbar('File removed', {
            variant: 'success'
          });
        }
      } finally {
        setIsLoading(false);
        onDeletingEnd();
      }
    }
  };


  const label = isLoading
    ? 'Removing corrupt file...'
    : 'Remove corrupt file';


  return <>
    <MobileLabel>{label}</MobileLabel>
    <Tooltip
      arrow
      title={label}
    >
      <FeatureBox loading={isLoading || isDeleted} contentLoadingOpacity={0}>
        <Box>
          <ActionButton
            color='primary'
            onClick={handleClick}
          >
            <Icon type='delete' />
          </ActionButton>
        </Box>
      </FeatureBox>
    </Tooltip>

    {/* delete file confirm dialog */}
    <ConfirmDialog
      titleStyle="warning"
      title='Confirm Removal'
      titleIcon='warning'
      content="Are you sure you want to ignore this corrupt file? Press OK to remove the file from your records."
      setConfirm={setConfirmDeleteFile}
    />
  </>;
}


const RecoveryTable = (props) => {
  const {
    data,
    recoveryType,
    extraLabels,
    onItemResolvingStart,
    onItemResolvingEnd,
    onItemResolved,
    imageOnly,
    hideReplace
  } = props;
  const isMobileTable = useMediaQuery(theme => theme.breakpoints.down('md'), { noSsr: true });
  const hasNonActionData = extraLabels || data.some(item => item.fileName);

  return (
    <TableContainer component={Paper} sx={{ mt: 1, mb: 3, border: '1px solid' }}>
      <Table size='small'>
        {hasNonActionData &&
          <TableHead>
            <TableRow>
              {!hideReplace &&
                <TableCell />
              }
              <TableCell />{/* link */}
              <TableCell />{/* delete */}
              {extraLabels && extraLabels.map(label => <TableCell key={label}>{label}</TableCell>)}
              {data[0].fileName &&
                <FileTableCell>File</FileTableCell>
              }
            </TableRow>
          </TableHead>
        }
        <TableBody>
          {data.map((item, idx) => {
            const handleResolvingStart = () => onItemResolvingStart(recoveryType, item.id)
            const handleResolvingEnd = () => onItemResolvingEnd(recoveryType, item.id)
            const handleResolved = () => onItemResolved(recoveryType, item.id)
            return <TableRow key={item.id}>
              {isMobileTable &&
                <TableCell sx={{ backgroundColor: 'action.hover' }}>
                  File #{idx + 1}
                </TableCell>
              }
              {!hideReplace &&
                <ActionTableCell>
                  <ReplaceWidget
                    recoveryId={item.id}
                    recoveryType={recoveryType}
                    onReplacingStart={handleResolvingStart}
                    onReplacingEnd={handleResolvingEnd}
                    onReplaced={handleResolved}
                    imageOnly={imageOnly}
                  />
                </ActionTableCell>
              }
              <ActionTableCell>
                <LinkWidget to={item.to} />
              </ActionTableCell>
              <ActionTableCell>
                <DeleteWidget
                  recoveryId={item.id}
                  recoveryType={recoveryType}
                  onDeletingStart={handleResolvingStart}
                  onDeletingEnd={handleResolvingEnd}
                  onDeleted={handleResolved}
                />
              </ActionTableCell>
              {item.extraInfo && item.extraInfo.map((cell, idx) =>
                <React.Fragment key={idx}>
                  {(!isMobileTable || cell) &&
                    <TableCell><MobileLabel>{extraLabels[idx]}</MobileLabel>{cell}</TableCell>
                  }
                </React.Fragment>
              )}
              {item.fileName &&
                <FileTableCell><MobileLabel>File</MobileLabel>{item.fileName}</FileTableCell>
              }
              {!isMobileTable && !hasNonActionData &&
                <TableCell />
              }
            </TableRow>
          }
          )}
        </TableBody>
      </Table>
    </TableContainer>
  )
}


const RecoveryAccountDocs = (props) => {
  const { label, items, onItemResolvingStart, onItemResolvingEnd, onItemResolved } = props;

  return (
    <Box>
      <SectionTitle>{label}</SectionTitle>
      <RecoveryTable
        data={items.map(item => ({
          id: item.id,
          fileName: item.userFile.fileName,
          extraInfo: [truncate(item.userFile.category.name)],
          to: '/profile/edit/docs',
        }))}
        recoveryType={RECOVERY_TYPES.ACCOUNT}
        extraLabels={['Category']}
        onItemResolvingStart={onItemResolvingStart}
        onItemResolvingEnd={onItemResolvingEnd}
        onItemResolved={onItemResolved}
      />
    </Box>
  )
}


const RecoveryDeptLogo = (props) => {
  const { label, items, onItemResolvingStart, onItemResolvingEnd, onItemResolved } = props;

  return (
    <Box>
      <SectionTitle>{label}</SectionTitle>
      <RecoveryTable
        data={items.map(item => ({
          id: item.id,
          to: '/profile/edit/photo',
        }))}
        recoveryType={RECOVERY_TYPES.DEPARTMENT_LOGO}
        onItemResolvingStart={onItemResolvingStart}
        onItemResolvingEnd={onItemResolvingEnd}
        onItemResolved={onItemResolved}
        imageOnly
        hideReplace
      />
    </Box>
  )
}


const RecoveryDogDocs = (props) => {
  const { label, items, onItemResolvingStart, onItemResolvingEnd, onItemResolved } = props;

  return (
    <Box>
      <SectionTitle>{label}</SectionTitle>
      <RecoveryTable
        data={items.map(item => ({
          id: item.id,
          fileName: item.dogFile.fileName,
          extraInfo: [truncate(item.dog.name), truncate(item.dogFile.category.name)],
          to: `/dogs/${item.dog.id}/edit/docs`,
        }))}
        recoveryType={RECOVERY_TYPES.DOG}
        extraLabels={['K9', 'Category']}
        onItemResolvingStart={onItemResolvingStart}
        onItemResolvingEnd={onItemResolvingEnd}
        onItemResolved={onItemResolved}
      />
    </Box>
  )
}


const RecoveryDogPhotos = (props) => {
  const { label, items, onItemResolvingStart, onItemResolvingEnd, onItemResolved } = props;

  return (
    <Box>
      <SectionTitle>{label}</SectionTitle>
      <RecoveryTable
        data={items.map(item => ({
          id: item.id,
          to: `/dogs/${item.dog.id}/edit/photo`,
          extraInfo: [truncate(item.dog.name)],
        }))}
        recoveryType={RECOVERY_TYPES.DOG_PHOTO}
        extraLabels={['K9']}
        onItemResolvingStart={onItemResolvingStart}
        onItemResolvingEnd={onItemResolvingEnd}
        onItemResolved={onItemResolved}
        imageOnly
        hideReplace
      />
    </Box>
  )
}


const RecoveryCompletionFiles = (props) => {
  const { label, items, onItemResolvingStart, onItemResolvingEnd, onItemResolved } = props;

  return (
    <Box>
      <SectionTitle>{label}</SectionTitle>
      <RecoveryTable
        data={items.map(item => ({
          id: item.id,
          fileName: item.completionFile.fileName,
          extraInfo: [
            truncate(item.eventName),
            format(item.eventDate, 'MMM dd, yyyy'),
            truncate(item.exerciseName),
            truncate(item.dog.name),
          ],
          to: makeLocation(
            'records',
            'training',
            'editAnswer', {
            exerciseId: item.exerciseId,
            answerId: item.completionId
          }),
        }))}
        recoveryType={RECOVERY_TYPES.TRAINING}
        extraLabels={['Event', 'Date', 'Exercise', 'K9']}
        onItemResolvingStart={onItemResolvingStart}
        onItemResolvingEnd={onItemResolvingEnd}
        onItemResolved={onItemResolved}
      />
    </Box>
  )
}


const RecoveryDeploymentFiles = (props) => {
  const { label, items, onItemResolvingStart, onItemResolvingEnd, onItemResolved } = props;

  return (
    <Box>
      <SectionTitle>{label}</SectionTitle>
      <RecoveryTable
        data={items.map(item => ({
          id: item.id,
          fileName: item.deploymentFile.fileName,
          extraInfo: [
            truncate(item.caseNumber),
            format(item.date, 'MMM dd, yyyy'),
          ],
          to: makeLocation(
            'records',
            'deployment',
            'edit', {
            deploymentId: item.deploymentId,
            viewSectionId: Types.DeploymentSection.SUMMARY,
            editSectionId: Types.DeploymentSection.SUMMARY
          }),
        }))}
        recoveryType={RECOVERY_TYPES.DEPLOYMENT}
        extraLabels={['Case Number', 'Date']}
        onItemResolvingStart={onItemResolvingStart}
        onItemResolvingEnd={onItemResolvingEnd}
        onItemResolved={onItemResolved}
      />
    </Box>
  )
}


const RecoverySeizedItems = (props) => {
  const { label, items, onItemResolvingStart, onItemResolvingEnd, onItemResolved } = props;

  return (
    <Box>
      <SectionTitle>{label}</SectionTitle>
      <RecoveryTable
        data={items.map(item => ({
          id: item.id,
          extraInfo: [
            truncate(item.caseNumber),
            format(item.date, 'MMM dd, yyyy'),
            truncate(item.environmentName),
            truncate(item.indicationOdor),
            truncate(item.indicationOdorType),
          ],
          to: makeLocation(
            'records',
            'deployment',
            'edit', {
            deploymentId: item.deploymentId,
            viewSectionId: Types.DeploymentSection.DETECTION,
            editSectionId: Types.DeploymentSection.DETECTION
          }),
        }))}
        recoveryType={RECOVERY_TYPES.INDICATION}
        extraLabels={['Case Number', 'Date', 'Environment', 'Odor', 'Odor Type']}
        onItemResolvingStart={onItemResolvingStart}
        onItemResolvingEnd={onItemResolvingEnd}
        onItemResolved={onItemResolved}
        imageOnly
      />
    </Box>
  )
}


const RecoveryClassFiles = (props) => {
  const { label, items, onItemResolvingStart, onItemResolvingEnd, onItemResolved } = props;

  return (
    <Box>
      <SectionTitle>{label}</SectionTitle>
      <RecoveryTable
        data={items.map(item => ({
          id: item.id,
          fileName: item.classFile.fileName,
          extraInfo: [
            truncate(item.name),
            format(item.date, 'MMM dd, yyyy'),
          ],
          to: makeLocation('records', 'class', 'edit', { classId: item.classId }),
        }))}
        recoveryType={RECOVERY_TYPES.CLASS}
        extraLabels={['Name', 'Date']}
        onItemResolvingStart={onItemResolvingStart}
        onItemResolvingEnd={onItemResolvingEnd}
        onItemResolved={onItemResolved}
      />
    </Box>
  )
}


const BulkUploadBox = styled(Box)(({ theme }) => ({
  padding: `${theme.spacing(1)} ${theme.spacing(1)}`,
  transition: `${theme.transitions.duration.short}ms ${theme.transitions.easing.easeOut}`,
  outline: 'none',
  border: `dashed 4px ${theme.palette.grey.colors[7]}`,
  borderRadius: '5px',
  flexDirection: 'column',
  justifyContent: 'center',
  cursor: 'pointer',
  '&.dragaccept': {
    background: theme.palette.grey.colors[8],
    border: `dashed 4px ${theme.palette.grey.colors[5]}`,
    cursor: 'grab',
  },
}));


function BulkUpload(props) {
  const { sections, disabled, onLoadStart, onLoadEnd } = props;
  const [matchCount, setMatchCount] = React.useState(null);
  const [isLoading, setIsLoading] = React.useState(false);
  const [uploadedCount, setUploadedCount] = React.useState(0);


  const flatSortedItems = React.useMemo(() => {
    const sectionsWithFiles = sections.filter(section => RECOVERY_TYPE_FILE_FIELD_NAMES[section.type] !== undefined);
    const flatItems = sectionsWithFiles.reduce(
      (fi, section) => fi.concat(section.items.map(item => ({
        recoveryId: item.id,
        recoveryType: section.type,
        fileName: item[RECOVERY_TYPE_FILE_FIELD_NAMES[section.type]].fileName,
      }))),
      []
    );
    return flatItems.sort((a, b) => (a.fileName > b.fileName) ? 1 : ((b.fileName > a.fileName) ? -1 : 0));
  }, [sections]);


  const findMatches = (files) => {
    // Find matches by advancing in parallel through item and file lists sorted by filename. (More efficient
    // than nested loops, assuming the the sort algorithms aren't terrible.)

    const sortedFiles = files.toSorted((a, b) => (a.name > b.name) ? 1 : ((b.name > a.name) ? -1 : 0));

    let itemIdx = 0;
    let fileIdx = 0;
    const matches = [];

    while (itemIdx < flatSortedItems.length && fileIdx < sortedFiles.length) {
      const item = flatSortedItems[itemIdx];
      const file = sortedFiles[fileIdx];

      if (fileIdx > 0 && file.name === sortedFiles[fileIdx - 1].name) {
        // Ignore duplicates in the list of uploaded files. Trying to replace the same file with 2 different
        // versions near-simultaneously will result in an error.
        fileIdx++;
      } else if (item.fileName === file.name) {
        // Match found. Advance through the item list, but stay on the same uploaded file to see if it matches
        // any other items.
        matches.push({ ...item, file });
        itemIdx++;
      } else if (item.fileName > file.name) {
        // The file item is ahead. Advance the file list.
        fileIdx++;
      } else {
        // The file list is ahead. Advance the item list.
        itemIdx++;
      }
    }

    return matches;
  };


  const { getRootProps, getInputProps, isDragAccept } = useDropzone({
    multiple: true,
    onDropAccepted: (files) => {
      setIsLoading(true);
      onLoadStart();
      setMatchCount(null);
      setUploadedCount(0);

      const matches = findMatches(files);
      setMatchCount(matches.length);

      (async () => {
        await Promise.all(matches.map(match =>
          uploadFile({
            ...match,
            onSuccess: () => setUploadedCount(p => p + 1),
          })
        ));

        setIsLoading(false);
        onLoadEnd();
      })();


    },
  });


  return <>
    <SectionTitle>Bulk Upload (Optional)</SectionTitle>
    <Card sx={{ border: '1px solid', mt: 1 }}>
      <CardContent>
        <IntroParagraph>
          Upload multiple files at the same time.
          Any corrupt files with the same names will
          be automatically replaced. Some devices may respond slowly
          if you select too many images or include videos.
        </IntroParagraph>
        <FeatureBox loading={isLoading || disabled}>
          <BulkUploadBox
            {...getRootProps({ className: `flex-hc${isDragAccept ? ' dragaccept' : ''}` })}
          >
            <input {...getInputProps()} />
            {isUsingTouchInput()
              ? <>
                <Typography variant="subtitle1">Tap Here</Typography>
                <Typography variant="subtitle2" style={{ lineHeight: 0.8 }}>to</Typography>
                <Typography variant="subtitle1">Select Files</Typography>
              </>
              : <>
                <Typography variant="subtitle1">Drop Files Here</Typography>
                <Typography variant="subtitle2" style={{ lineHeight: 0.8 }}>or</Typography>
                <Typography variant="subtitle1">Click To Select</Typography>
              </>
            }
          </BulkUploadBox>
        </FeatureBox>

        {matchCount !== null &&
          <Box
            sx={{
              marginTop: 2,
              color: 'primary.main',
            }}
          >
            {matchCount > 0
              ? <div className='flex-hc'>
                <div>{uploadedCount}/{matchCount} matched files replaced</div>
                <Icon
                  type='complete'
                  color='success'
                  sx={{
                    marginLeft: 1,
                    visibility: (!isLoading && uploadedCount > 0) ? 'visible' : 'hidden'
                  }}
                />
              </div>
              : <>No files matched</>
            }
          </Box>
        }
      </CardContent>
    </Card>
  </>;
}


const ComponentMap = {
  [RECOVERY_TYPES.ACCOUNT]: RecoveryAccountDocs,
  [RECOVERY_TYPES.DEPARTMENT_LOGO]: RecoveryDeptLogo,
  [RECOVERY_TYPES.DOG]: RecoveryDogDocs,
  [RECOVERY_TYPES.DOG_PHOTO]: RecoveryDogPhotos,
  [RECOVERY_TYPES.TRAINING]: RecoveryCompletionFiles,
  [RECOVERY_TYPES.DEPLOYMENT]: RecoveryDeploymentFiles,
  [RECOVERY_TYPES.INDICATION]: RecoverySeizedItems,
  [RECOVERY_TYPES.CLASS]: RecoveryClassFiles
};


const IntroParagraph = styled(Typography)(({ theme }) => ({
  marginBottom: theme.spacing(2),
  display: 'flex',
}));


export default function Recovery(props) {
  const isMobile = useMediaQuery(theme => theme.breakpoints.down('sm'), { noSsr: true });
  const { setAppBar } = React.useContext(AppBarContext);
  const [isLoading, setIsLoading] = React.useState(true);
  const [itemResolvingCount, setItemResolvingCount] = React.useState(0);
  const [sections, setSections] = React.useState();


  const updateData = async (background) => {
    if (!background) {
      setIsLoading(true);
    }
    const response = await axios.get('/recovery');
    if (response.isSuccess) {
      setSections(response.data);
    }
    if (!background) {
      setIsLoading(false);
    }
  };


  // init page
  React.useEffect(() => {
    const actions = [<BarButton
      onClick={() => updateData()}
      children={<Icon type='refresh' />}
    />];
    document.title = 'PACKTRACK - File Recovery';
    setAppBar('File Recovery', actions);
  }, [setAppBar]);


  // query for recovery items
  React.useEffect(() => {
    updateData();
  }, []);


  const handleItemResolvingStart = () => {
    setItemResolvingCount(p => p + 1);
  };

  const handleItemResolvingEnd = () => {
    setItemResolvingCount(p => p - 1);
  };

  const handleItemResolved = (type, id) => {
    setSections(sections.map(section => section.type === type
      ? {
        ...section,
        items: section.items.map(item => item.id === id
          ? {
            ...item,
            isHidden: true,
          }
          : item
        ),
      }
      : section
    ));
  };


  const filterVisibleItems = (items) => items.filter(item => (!item.dateTimeResolved && !item.isHidden));

  let filteredSections = null;
  let itemCount = null;

  if (sections) {
    filteredSections = sections.map(section => ({
      ...section,
      items: filterVisibleItems(section.items),
    }));

    itemCount = filteredSections.reduce(
      (total, section) => total + section.items.length,
      0
    );
  }


  return (
    <>
      <Container data-testid='recovery-page' sx={{ pt: { xs: 2, sm: 2, md: 3, lg: 4, xl: 4 } }}>

        {/* desktop title */}
        {!isMobile &&
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            mb={3}
          >
            <PageTitle iconName='copy' title='File Recovery' />
            <Button
              children='Refresh'
              startIcon={<Icon type='refresh' />}
              onClick={() => updateData()}
              disabled={isLoading}
            />
          </Box>
        }

        {filteredSections !== null &&
          <>
          {itemCount === 0
            ? <IntroParagraph variant='subtitle1'>No files have been identified as corrupt.</IntroParagraph>
            : <IntroParagraph variant='subtitle1' sx={{ alignItems: 'flex-start', marginBottom: 5 }}>
              <Box
                sx={{
                  display: 'inline',
                  border: '2px solid',
                  borderRadius: 2,
                  marginTop: -0.5,
                  padding: '6px 8px',
                  fontWeight: 'bold',
                  lineHeight: 1,
                  marginRight: 1
                }}
              >
                {itemCount}
              </Box>
              {itemCount !== 1 ? 'files are' : 'file is'} corrupt.
            </IntroParagraph>
          }


          <Box mb={4}>
            <Accordion>
              <AccordionSummary expandIcon={<Icon type='expander' />}>
                <Typography variant='h6'>Background on this incident</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <IntroParagraph>
                  Thank you for being a Member of the PackTrack Team. It is our continued pleasure to serve you 
                  and your agency providing the #1 Online K9 Record Keeping System.
                </IntroParagraph>
                <IntroParagraph>
                  Our proactive monitoring routines and tools recently found a small number of corrupt 
                  image/picture files in your subscription. They are unable to be viewed or printed. 
                  Our investigation found that our server on AWS (Amazon Web Servers) had a faulty server 
                  incident that corrupted the files. AWS is the world leader in providing Cloud based data 
                  center services.
                </IntroParagraph>
                <IntroParagraph>
                  The information gained through our proactive investigation has led us to several AWS 
                  technological improvements that will reduce the risk of this happening again.
                </IntroParagraph>
                <IntroParagraph>
                  To help address this issue, we have created an easy way to re-upload the specific 
                  images/pictures to PackTrack. Once completed, this event will be resolved and your files 
                  will be restored.
                </IntroParagraph>
                <IntroParagraph>
                  On your App or PC, please log in. On the left side of the screen you will see a button 
                  labeled File Recovery. Please use this File Recovery menu option on the web site and mobile 
                  app to re-upload your affected files. There are simple detailed instructions provided.
                </IntroParagraph>
                <IntroParagraph>
                  We sincerely apologize for this situation and any inconvenience it may have caused. 
                  If you have any questions or concerns, please don’t hesitate to contact us.
                </IntroParagraph>
              </AccordionDetails>
            </Accordion>

            <Accordion>
              <AccordionSummary expandIcon={<Icon type='expander' />}>
                <Typography variant='h6'>Upload instructions</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <IntroParagraph>
                  <Box component='span' mr={1}><Icon type='upload' /></Box>
                  Files with an upload button can be uploaded and replaced in one step directly from this page.
                </IntroParagraph>
                <IntroParagraph>
                  <Box component='span' mr={1}><Icon type='open-link' /></Box>
                  All files can also be uploaded by pressing this button which takes you to the corrupt file location.
                  If you upload a new file there, you may also have to delete the corrupt one.
                </IntroParagraph>
                <IntroParagraph sx={{ marginBottom: 3 }}>
                  <Box component='span' mr={1}><Icon type='delete' /></Box>
                  If you don't want to replace a file then press the "Remove Corrupt File" button to ignore it.
                </IntroParagraph>
                <IntroParagraph>
                  <Box component='span' mr={1}><Icon type='refresh' /></Box>
                  The list below will update as you replace corrupt files.
                  A file is considered fixed when the entry disappears from the list.
                  If the list doesn't reflect changes you made elsewhere, press the "Refresh" button to reload it.
                </IntroParagraph>
                <IntroParagraph>
                  <span>
                    For some documents, the reupload process will be easiest from a desktop computer or laptop.
                    However, you can also upload replacement pictures directly from the app on your phone.
                    Open the PACKTRACK app and choose the new "File Recovery" menu option to access this page.
                    <strong>Please <MuiLink
                      component='span'
                      onClick={() => window.Intercom('showNewMessage')}
                      sx={{ cursor: 'pointer' }}
                    >
                      contact us
                    </MuiLink> if
                      you have any questions.</strong> We can also get on a call to walk you through it.
                  </span>
                </IntroParagraph>
              </AccordionDetails>
            </Accordion>
          </Box>

          {itemCount > 0 &&
            <>
              <BulkUpload
                sections={filteredSections}
                disabled={itemResolvingCount > 0}
                onLoadStart={() => setIsLoading(true)}
                onLoadEnd={() => { setIsLoading(false); updateData() }}
              />

              <FeatureBox style={{ minHeight: 140 }} loading={isLoading}>
                {filteredSections?.map(section => {
                  const SectionComp = ComponentMap[section.type];
                  return section.items.length > 0
                    ? <SectionComp
                      key={section.type}
                      label={section.label}
                      items={section.items}
                      onItemResolvingStart={handleItemResolvingStart}
                      onItemResolvingEnd={handleItemResolvingEnd}
                      onItemResolved={handleItemResolved}
                    />
                    : null;
                })}
              </FeatureBox>
            </>
          }
          </>
        }
      </Container>
    </>
  );
}